<template>
  <o-dropdown
    dropdown-class="bg-white dark:bg-gray-900 rounded-md shadow-md z-80000 overflow-hidden"
    :close-on-dropdown-click="false"
  >
    <text-field
      :value="displayValue"
      :disabled="disabled"
      icon="CalendarIcon"
    />

    <template #content>
      <div class="flex">
        <div class="p-2">
          <client-only>
            <UCalendar
              v-model.range="calendarRange"
              :locale="$i18n.locale.value"
              :min-value="minCalendarDate"
              :week-starts-on="weekStartsOn"
              :number-of-months="numberOfMonths"
              class="inline-block align-top"
            />
          </client-only>
        </div>
        <div class="inline-block bg-gray-100 dark:bg-gray-800 p-3 align-top mt-3 md:mt-0 w-full md:w-auto">
          <text-field :model-value="displayStart" readonly />
          <text-field :model-value="displayEnd" readonly class="mt-2" />
        </div>
      </div>
    </template>
  </o-dropdown>
</template>

<script setup lang="ts">
import { CalendarDate, type DateValue } from '@internationalized/date'
import TextField from './Text.vue'
import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'
import ODropdown from '@/components/elements/dropdown/Dropdown.vue'

const { $auth, $date, $dateFns, $i18n } = useNuxtApp()

interface Props {
  modelValue?: Record<string, any> | null
  disabled?: boolean
  placeholder?: string | null
  minDate?: Date | null
  augmenter?: Record<string, any>
  options?: any[] | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  disabled: false,
  placeholder: null,
  minDate: null,
  augmenter: () => Augmenters.YMD,
  options: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

const weekStartsOn = useCalendarFirstDayOfWeek(() => ($auth as any)?.info?.location?.startDayOfWeek)

const numberOfMonths = ref(1)

onMounted(() => {
  numberOfMonths.value = window.innerWidth >= 1024 ? 2 : 1
})

function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

function calendarDateToDate(cd: DateValue): Date {
  return new Date(cd.year, cd.month - 1, cd.day)
}

const minCalendarDate = computed(() => {
  if (!props.minDate) return undefined
  return dateToCalendarDate(props.minDate)
})

const calendarRange = computed({
  get(): { start: DateValue; end: DateValue } | undefined {
    if (!props.modelValue) return undefined

    const aug = props.augmenter
    const startRaw = (props.modelValue as any).start
    const endRaw = (props.modelValue as any).end

    if (startRaw == null || endRaw == null) return undefined

    const startDate = aug?.input ? aug.input(startRaw) : startRaw
    const endDate = aug?.input ? aug.input(endRaw) : endRaw

    if (!(startDate instanceof Date) || !(endDate instanceof Date)) return undefined

    return {
      start: dateToCalendarDate(startDate),
      end: dateToCalendarDate(endDate),
    }
  },
  set(value: { start: DateValue; end: DateValue } | undefined) {
    if (!value) {
      emit('update:modelValue', null)
      return
    }

    const startDate = calendarDateToDate(value.start)
    const endDate = calendarDateToDate(value.end)
    const aug = props.augmenter

    emit('update:modelValue', {
      start: aug?.output ? aug.output(startDate) : startDate,
      end: aug?.output ? aug.output(endDate) : endDate,
    })
  },
})

const displayStart = computed(() => {
  const range = calendarRange.value
  if (!range) return ''
  const date = calendarDateToDate(range.start)
  return ($date as any).format(date, ($date as any).presets.short)
})

const displayEnd = computed(() => {
  const range = calendarRange.value
  if (!range) return ''
  const date = calendarDateToDate(range.end)
  return ($date as any).format(date, ($date as any).presets.short)
})

const displayValue = computed(() => {
  if (displayStart.value && displayEnd.value) {
    return `${displayStart.value} to ${displayEnd.value}`
  }
  return ''
})
</script>
